// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import dtmfWorklet from '!file-loader!./goloom-dtmf-analyser-worklet';
import { Event } from '@yandex-int/messenger.utils';

import { bind } from '../decorators';
import { BaseError } from '../errors';

interface DtmfWorkletMessageEvent extends MessageEvent {
    data: string;
}

const AGGREGATION_TIMEOUT = 150;

export class DTMFAnalyser {
    private audioCtx: AudioContext;
    private _audioWorkletNode?: AudioWorkletNode;
    private connectedNodes = 0;
    private initPromise: Promise<void>;
    public onDtmfChar = new Event<string>();

    private timeoutId?: ReturnType<typeof window.setTimeout>;
    private aggregationMap = new Map<string, number>();

    constructor(audioCtx: AudioContext) {
        this.audioCtx = audioCtx;
        this.initPromise = this.init();
    }

    private get audioWorkletNode(): AudioWorkletNode {
        if (!this._audioWorkletNode) {
            throw new BaseError('AudioWorkletNode has not been created', { method: 'get:audioWorkletNode' });
        }

        return this._audioWorkletNode;
    }

    /**
     * Анализатор довольно шумный. При нажатии на кнопку придет сразу ~10 сигналов,
     * причем значение части из них может отличаться от реального. Поэтому делаем агрегацию
     * считая сигналов какой dtmf кнопки пришло больше всего за окно времени.
     */
    @bind
    private handleAnalyserMessage({ data }: DtmfWorkletMessageEvent) {
        if (!this.timeoutId) {
            this.timeoutId = setTimeout(() => {
                const char = [...this.aggregationMap.entries()].sort((a, b) => b[1] - a[1])[0][0];

                this.aggregationMap.clear();
                this.timeoutId = undefined;

                this.onDtmfChar.dispatch(char);
            }, AGGREGATION_TIMEOUT);
        }

        this.aggregationMap.set(data, (this.aggregationMap.get(data) || 0) + 1);
    }

    private async init() {
        await this.audioCtx.audioWorklet.addModule(dtmfWorklet);

        this._audioWorkletNode = new AudioWorkletNode(this.audioCtx, 'goloom-audio-core-dtmf');
        this._audioWorkletNode.port.start();
        this._audioWorkletNode.port.addEventListener('message', this.handleAnalyserMessage);

        const destinationNode = this.audioCtx.createGain();
        destinationNode.gain.value = 0;

        this._audioWorkletNode.connect(destinationNode);
        destinationNode.connect(this.audioCtx.destination);
    }

    public async connect(sourceNode: AudioNode) {
        await this.initPromise;

        if (this.connectedNodes > 1) {
            throw new BaseError('Can not analyse more than 1 node. Create a new analyser instance.', {
                method: 'dtmf-analyser:connect',
            });
        }

        sourceNode.connect(this.audioWorkletNode);
    }

    public async disconnect(sourceNode: AudioNode) {
        await this.initPromise;

        sourceNode.disconnect(this.audioWorkletNode);
    }
}
